import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { clearErrors } from "../../redux/slices/authAdminSlice";
import { resetPassword } from "../../redux/actions/authActions";
import { RootState, AppDispatch } from "../../redux/store";
import LoginLogo from "../../assets/LoginLogo.png";
import { Spin, notification } from "antd";
import { useTranslation } from "react-i18next";

const customIcon = (
  <div className="w-[48px] h-[48px] border-[6px] border-solid border-t-[#212529] border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
);

const ResetPassword = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const { loading, error, passwordError } = useSelector(
    (state: RootState) => state.auth
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    dispatch(clearErrors());

    if (!token) {
      notification.error({
        message: t("resetpassword.invalidLinkTitle"),
        description: t("resetpassword.invalidLinkDesc"),
      });
      return;
    }

    const result = await dispatch(
      resetPassword({ newPassword, confirmNewPassword })
    );

    if (resetPassword.fulfilled.match(result)) {
      notification.success({
        message: t("resetpassword.successTitle"),
        description: t("resetpassword.successDesc"),
      });
      navigate("/login");
    } else {
      notification.error({
        message: t("resetpassword.failedTitle"),
        description: error || t("resetpassword.failedDesc"),
      });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-6 bg-bg-login/register">
      <img src={LoginLogo} alt="Logo" />
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div className="relative flex flex-col gap-2">
          <label className="text-sm">{t("resetpassword.passwordLabel")}</label>
          <div className="relative w-[312px]">
            <input
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t("resetpassword.passwordPlaceholder")}
              className="w-full h-[52px] rounded-xl pl-4 pr-10 text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 flex items-center right-3"
            >
              {showPassword ? (
                <IoEyeOutline size={18} />
              ) : (
                <IoEyeOffOutline size={18} />
              )}
            </button>
          </div>
          {passwordError && (
            <p className="text-sm text-red-500">{passwordError}</p>
          )}

          <label className="text-sm">{t("resetpassword.confirmLabel")}</label>
          <div className="relative w-[312px]">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              placeholder={t("resetpassword.confirmPlaceholder")}
              className="w-full h-[52px] rounded-xl pl-4 pr-10 text-sm"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 flex items-center right-3"
            >
              {showConfirmPassword ? (
                <IoEyeOutline size={18} />
              ) : (
                <IoEyeOffOutline size={18} />
              )}
            </button>
          </div>
          {passwordError && (
            <p className="text-sm text-red-500">{passwordError}</p>
          )}
        </div>

        {loading ? (
          <p className="text-center">
            <Spin indicator={customIcon} />
          </p>
        ) : (
          <button
            type="submit"
            className="w-[312px] h-[52px] bg-loginButton rounded-xl text-white mt-4"
          >
            {t("resetpassword.resetButton")}
          </button>
        )}

        {isSubmitting && (
          <div className="mt-4 flex items-center justify-center">
            <Spin indicator={customIcon} />
          </div>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </form>
    </div>
  );
};

export default ResetPassword;
